import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@mui/material';
import {
  capitalize
} from 'lodash';
import React from 'react';
import {
  FAVICON__TEN_BY_TEN
} from '../../dictionaries/App.dic';
import {
  Arr,
  join,
  sanitizeArr
} from '../../lib/Array.lib';
import {
  YES
} from '../../lib/Boolean.lib';
import Core from '../../lib/Core';
import {
  FAVICON__CRUNCHBASE
} from '../../lib/Crunchbase.lib';
import {
  escapeRegExp
} from '../../lib/GenericTools.lib';
import useState from '../../lib/hooks/useState.hook';
import countLoopbackRecord from '../../lib/services/BE/countLoopbackRecord.async';
import {
  COLLECTION__COMPANY,
  COLLECTION__SCHOOL,
  readLoopbackRecord
} from '../../lib/services/BE/loopback.api';
import {
  padWithZeros,
  Str
} from '../../lib/String.lib';
import EntityEdit from '../Candidates/Histories/EntityEdit';
import HISTORIES_DERIVATOR, {
  deleteEntity
} from '../Candidates/Histories/Histories.derivator';
import Avatar from '../Layout/Wrappers/Avatar';
import Box from '../Layout/Wrappers/Box';
import Button from '../Layout/Wrappers/Button';
import Checkbox from '../Layout/Wrappers/Checkbox';
import Fieldset from '../Layout/Wrappers/Fieldset';
import IconButton from '../Layout/Wrappers/IconButton';
import TextField from '../Layout/Wrappers/TextField';
import Title from '../Layout/Wrappers/Title';
import Typography from '../Layout/Wrappers/Typography';

const MAP__COLLECTION = {
  [COLLECTION__COMPANY]: {
    singular: 'company',
    plural: 'companies',
    derivator: HISTORIES_DERIVATOR.experience
  },
  [COLLECTION__SCHOOL]: {
    singular: 'school',
    plural: 'schools',
    derivator: HISTORIES_DERIVATOR.education
  },
}

export default function AccordionCandidateHistoryEntities({
  expand = false,
  collection = COLLECTION__COMPANY,
  ...props
}) {
  const mem = React.useRef({});
  const [state, updateState] = useState({
    exactMatches: true,
    page: 1,
    total: 0,
    limit: 20,
  });

  const CONTEXT = {
    ...MAP__COLLECTION[collection].derivator
  }
  const VALUE__KEYWORDS = Str(state.keywords);
  const LIST__SUGGESTIONS = Arr(state.suggestions);
  const LIST__SELECTED_ENTITY_IDS = LIST__SUGGESTIONS.filter(
    (entry) => (entry.checked)
  ).map(
    (entry) => (entry.id)
  );
  const TOTAL__SELECTED = LIST__SELECTED_ENTITY_IDS.length;
  const YES__SELECTED = TOTAL__SELECTED > 0;
  const YES__LOADING = YES(state.loading);
  const YES__EXACT_MATCHES = YES(state.exactMatches);
  const YES__TYPE_COMPANY = YES(collection === COLLECTION__COMPANY);
  const YES__TYPE_SCHOOL = YES(collection === COLLECTION__SCHOOL);
  const PAGE = state.page || 1;
  const LIMIT = state.limit || 20;
  const TOTAL = state.total || 0;

  const fetchSuggestions = async ({
    input = '',
    exactMatches = YES__EXACT_MATCHES,
    page = PAGE
  }) => {
    if (!input || input.length < 3) {
      await updateState({ suggestions: [], loading: false });
      return;
    }
    await updateState({ suggestions: [], loading: true });

    try {
      const skip = (page - 1) * LIMIT;

      const where = {
        [YES__TYPE_COMPANY ? 'name' : 'universityName']: (
          exactMatches
            ? { like: '^' + escapeRegExp(input) + '$', options: 'i' }
            : { like: escapeRegExp(input), options: 'i' }
        ),
      };

      // Fetch list of results
      const data = await readLoopbackRecord({
        collection,
        where,
        fields: sanitizeArr([
          'id',
          YES__TYPE_COMPANY && 'name',
          YES__TYPE_SCHOOL && 'universityName',
          'alternativeNames',
          'crunchbaseOrganizationId',
        ]),
        include: [
          {
            relation: 'crunchbaseOrganization',
            scope: {
              fields: [
                'id',
                'name',
                'logo_url'
              ]
            }
          }
        ],
        order: ['name ASC'],
        limit: LIMIT,
        skip,
        key: 'AccordionCandidateHistoryEntities__fetchSuggestions',
        mapper: (items) => Arr(items).map((
          item,
          index,
          data,
          LOGO__DEFAULT = (
            item.crunchbaseOrganizationId
              ? FAVICON__CRUNCHBASE
              : FAVICON__TEN_BY_TEN
          )
        ) => ({
          ...item,
          name: YES__TYPE_COMPANY ? item.name : item.universityName,
          logoUrl: (
            item.crunchbaseOrganization?.logo_url
            || LOGO__DEFAULT
          ),
          logoUrlError: LOGO__DEFAULT,
        }))
      });

      // Fetch count
      const count = await countLoopbackRecord({
        collection,
        where,
        key: 'AccordionCandidateHistoryEntities__fetchSuggestions__count'
      });

      await updateState({
        suggestions: Arr(data),
        loading: false,
        page,
        total: count
      });
    }
    catch (err) {
      console.error(err);
      Core.showError('Failed to fetch suggestions.');
      await updateState({
        suggestions: [],
        loading: false
      });
    }
  };

  const onChange = (field) => (event) => updateState({
    [field]: Str(event.target.value)
  });

  const nextPage = () => {
    updateState({ page: PAGE + 1 });
    fetchSuggestions({ input: VALUE__KEYWORDS, page: PAGE + 1 });
  };

  const prevPage = () => {
    if (PAGE > 1) {
      updateState({ page: PAGE - 1 });
      fetchSuggestions({ input: VALUE__KEYWORDS, page: PAGE - 1 });
    }
  };

  const onClickBulkDelete = (event) => {
    deleteEntity({
      context: CONTEXT,
      entityIds: LIST__SELECTED_ENTITY_IDS,
    }).then(() => {
      fetchSuggestions({ input: VALUE__KEYWORDS, page: PAGE });
      Core.showSuccess(`${capitalize(MAP__COLLECTION[collection].plural)} deleted!`);
    });
  }

  return (
    <Accordion
      {...props}
    >
      <AccordionSummary
        expandIcon={<i className='material-icons'>expand_more</i>}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        className='sticky-top bg-white'
      >
        <Title className='m-0'>
          {capitalize(MAP__COLLECTION[collection].plural)}
        </Title>
      </AccordionSummary>
      <AccordionDetails className='overflow-hidden'>
        <Box column>
          <Fieldset
            title='Keywords'
            subtitle={
              <>
                Type keywords to search {MAP__COLLECTION[collection].plural} from our database.
              </>
            }
          >
            <Box row className='flex-align-left-top'>
              <Box flex1
                className='mr-1'
              >
                <TextField multiline disabledBlurExport fullWidth
                  value={VALUE__KEYWORDS}
                  onChange={(event) => {
                    onChange('keywords')(event);
                    clearTimeout(mem.current.timeout);
                    mem.current.timeout = setTimeout(
                      () => fetchSuggestions({
                        input: event.target.value,
                        page: 1
                      }),
                      1000
                    );
                  }}
                  placeholder="Type at least 2 characters to search (e.g. 'Consult')"
                />
              </Box>
              <Checkbox
                checked={YES__EXACT_MATCHES}
                onChange={(event) => {
                  const exactMatches = event.target.checked;
                  updateState({
                    exactMatches,
                    page: 1
                  });
                  clearTimeout(mem.current.timeout);
                  mem.current.timeout = setTimeout(
                    () => fetchSuggestions({
                      input: VALUE__KEYWORDS,
                      exactMatches,
                      page: 1
                    }),
                    1000
                  );
                }}
                label="Exact match"
              />
            </Box>
          </Fieldset>

          <Fieldset
            title={
              <>
                Results{' '}
                {YES__LOADING && <span className="text-sm text-gray-500">(loading...)</span>}
              </>
            }
            actionBar={
              <Box row>
                <Button small outlined minW100
                  className='mr-1'
                  disabled={PAGE <= 1 || YES__LOADING}
                  onClick={prevPage}
                >
                  Previous
                </Button>
                <Typography small
                  className='mr-1'
                >
                  Page {PAGE} of {Math.ceil(TOTAL / LIMIT) || 1}
                </Typography>
                <Button small outlined minW100
                  disabled={PAGE * LIMIT >= TOTAL || YES__LOADING}
                  onClick={nextPage}
                >
                  Next
                </Button>
              </Box>
            }
          >
            <Box column
              className="border rounded overflow-auto"
              style={{ maxHeight: '27rem' }}
            >
              {LIST__SUGGESTIONS.length === 0 ? (
                <Box
                  className="placeholder p-2"
                >
                  No results. Try a different keyword.
                </Box>
              ) : (
                <>
                  <Box row
                    className='px-1 mb-1 border-bottom sticky-top bg-white-common'
                    style={{
                      minHeight: 48
                    }}
                  >
                    <Checkbox
                      checked={LIST__SUGGESTIONS.every((entry) => entry.checked)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        updateState({
                          suggestions: Arr(LIST__SUGGESTIONS).map((entry) => ({
                            ...entry,
                            checked
                          }))
                        });
                      }}
                      label={
                        <Box row>
                          <Typography className='fw-600 c-black-darker mr-1'>
                            Select all
                          </Typography>
                          <Typography className='text-sm text-gray-500'>
                            ({TOTAL__SELECTED}/{LIST__SUGGESTIONS.length})
                          </Typography>
                        </Box>
                      }
                      className='mr-2'
                    />
                    <IconButton
                      acl={YES__SELECTED}
                      icon='delete'
                      onClick={onClickBulkDelete}
                      className='mr-2'
                    />
                  </Box>
                  {
                    LIST__SUGGESTIONS.map((
                      entity,
                      index,
                      data,
                      YES__SELECTED = YES(entity.checked)
                    ) => (
                      <Box row key={entity.id} className='space-between mb-1 hover-bg pr-2'>
                        <Box row wAuto className='p-1'>
                          <Checkbox
                            checked={YES__SELECTED}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              updateState({
                                suggestions: Arr(data).map((entry) => ({
                                  ...entry,
                                  checked: entry.id === entity.id
                                    ? checked
                                    : entry.checked
                                }))
                              });
                            }}
                            label={padWithZeros(index + 1, 2)}
                            labelProps={{ className: 'w-auto' }}
                          />
                        </Box>
                        <Box row flex1>
                          <Avatar
                            src={entity.logoUrl}
                            errorSrc={entity.logoUrlError}
                            className='mr-2'
                          />
                          <Box row className=''>
                            <Typography className='fw-600 c-black-darker mr-1'>
                              {entity.name}
                            </Typography>
                            <Typography className='fw-300 f-sm c-cyan'>
                              {join(entity.alternativeNames)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box row>
                          <EntityEdit
                            className='ml-auto'
                            title={`Edit tags and alternative names of ${(
                              entity.name || `the ${MAP__COLLECTION[collection].singular}`
                            )}`}
                            entityId={entity.id}
                            onSave={(entity) => {
                              fetchSuggestions({ input: VALUE__KEYWORDS, page: PAGE });
                            }}
                            onDelete={() => {
                              fetchSuggestions({ input: VALUE__KEYWORDS, page: PAGE });
                            }}
                            context={CONTEXT}
                          >
                            <IconButton
                              icon='edit'
                            />
                          </EntityEdit>
                        </Box>
                      </Box>
                    ))

                  }
                </>
              )}
            </Box>

          </Fieldset>

        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
